import { create } from 'zustand';
import { Account, ComposeRestore, Email, EmailDensity, EmailFilter, EmailUiPrefs, UILang, Folder, FolderInfo, LabelOverride, LabelRule, Me, ReplyMode } from '../types';
import { api, setBooksSeat, setAuditorSeat } from '../utils/api';
import { labelView } from '../utils/emailLabels';
import i18n, { resolveInitialLang } from '../i18n';
import { bindActiveCompanyUser, bindActiveCompanyFromToken, clearActiveCompany, getActiveCompanyId } from '../services/activeCompany';
import { setItemWithRoom } from '../services/draftMirror';
import { setBrand } from '../utils/brand';
import { setZoneCity } from '../utils/zoneCity';
import { setCurrencyWords } from '../utils/currencyWord';

interface AppState {
  uiLang: UILang;
  me: Me | null;
  accounts: Account[];
  folders: Record<string, FolderInfo[]>;  // mailbox/label list by accountId
  // PandaAI-only label rename/colour/delete by accountId → label path (never written to Gmail).
  labelOverrides: Record<string, Record<string, LabelOverride>>;
  // Unread emails per label path, by accountId — a label row with unread mail shows bold + a count.
  labelUnread: Record<string, Record<string, number>>;
  // Unread emails per fixed folder (sent, spam, …), by accountId — 'all' while no mailbox is open. A
  // folder row with unread mail shows bold, like a label row. Drafts counts every draft (never unread).
  folderUnread: Record<string, Record<string, number>>;
  emails: Email[];
  // Which question those rows answer (emailsKey): the mailbox, folder and search they were fetched for. The search box
  // and the folder change as they are pressed, and the rows only when the answer comes, or never if it fails.
  emailsFor: string;
  totalEmails: number;
  emailLimit: number;       // how many list rows to fetch (grows by 50 via "Load more")
  isLoadingMore: boolean;
  activeAccountId: string | null;
  activeEmailId: string | null;
  // Snapshot of the active row — keeps the reading pane alive when a background refresh
  // drops the row from the loaded page (unread filter, search, paging window).
  activeEmailPin: Email | null;
  activeFolder: Folder;
  replyMode: ReplyMode;
  searchQuery: string;
  // The search bubble's filter panel (Calem, report 22bdcef3) — From / To / Subject / Has the words /
  // Doesn't have / Has attachment / a date range. {} = untouched, and then the list asks for exactly
  // what it always did. Every box narrows the list alongside searchQuery, the open folder and the open
  // mailbox, and shows as a removable chip beside the bubble. Not persisted: a filter is a question
  // somebody is asking now, not a setting.
  emailFilter: EmailFilter;
  // Gmail-style conversation view: on (default) groups same-thread messages into one row; off
  // shows every message as its own flat row. Choice persists (localStorage mf_conversationView).
  conversationView: boolean;
  // Email list row density — Default / Comfortable / Compact. Look only: the same rows load either
  // way. Choice persists (localStorage mf_emailDensity).
  emailDensity: EmailDensity;
  // The Reply all switch (Calem, report 643e2141): on, every Reply button opens as Reply All does (replyModeOf) —
  // the sender, To and Cc, less the mailbox's own address. Off unless switched on. Per user on the server, with
  // this browser's copy (localStorage mf_replyAllDefault) for the viewers that open a reply without MailApp.
  replyAllDefault: boolean;
  // The mailbox the module opens on (and falls back to when the open one is removed) — while it is
  // still in the list; otherwise the top account. Per user (localStorage mf_defaultAccount:<userId>);
  // pinned to the top of the ACCOUNTS list. null = none chosen.
  defaultAccountId: string | null;
  // The user's own ACCOUNTS order (account ids), set by dragging rows in the sidebar. Per user
  // (localStorage mf_accountOrder:<userId>); [] = the server's order. See orderedAccounts.
  accountOrder: string[];
  // The folder list's More/Less row: true shows the `more` folders (all but Inbox and Sent).
  // Choice persists (localStorage mf_navMore2 — renamed from mf_navMore when Drafts joined the `more`
  // folders, so every list started folded once; Calem, Bugify #c1ff895b).
  navMore: boolean;
  // The ACCOUNTS list's More (n) / Less toggle: true shows the mailboxes with nothing waiting as well —
  // no unread Inbox mail, draft or other folder count (the open one always shows). Choice persists
  // (localStorage mf_acctMore; Calem, Bugify #b3f5fa23, #c1ff895b).
  acctMore: boolean;
  // The Email notifications switch (Calem, report 2e5c8ac7): the new-mail pop-up at the bottom right of
  // every page (NewMailToast), for the default mailbox's Inbox. On unless switched off. Kept on the server
  // only, which decides by it — no localStorage copy, as one office browser serves several people and a
  // copy here would be handed on to the next person's saved layout (loadEmailPrefs sends up what the
  // server lacks). This one draws the switch.
  notifyNewMail: boolean;
  // Undo send (Calem, report ef1507ed): the seconds a sent email waits — "Sending in N s… Undo" at the bottom left
  // (components/UndoSend) — before it is posted; 0 = Off, posted at once. Picked in the composer's Send menu and kept
  // on the server only, like notifyNewMail. sendQueue holds the emails waiting, undoneSends the ones whose Undo was
  // pressed — their composer is open again.
  undoSendSecs: number;
  sendQueue: QueuedSend[];
  undoneSends: UndoneSend[];
  // The Email phone app's Inbox / Unread choice (report 0e8dd0cb): what its list opens on. Only the two buttons on its
  // heading row change it. Kept on the server only, like notifyNewMail.
  emailAppUnread: boolean;
  // Bumped each time the saved layout arrives from the server (loadEmailPrefs) — MailApp then re-reads
  // the column widths, split screen and sidebar pin it keeps itself.
  emailPrefsVersion: number;
  // The mailboxes PandaAI Email may show in this browser tab: the ones picked for the Quick Launch focus dashboard
  // (Calem, report 0b314fb0: "allow them to select one only (or more if they choose) email client"), set while that
  // tab shows it (components/FocusDashboard). null everywhere else — every mailbox the user can open. Applied where
  // the account list is loaded, so the sidebar, the rail and the From lists offer only those, and the open mailbox
  // is always one of them.
  mailboxFilter: string[] | null;
  toastMessage: string | null;
  blockingError: string | null;   // modal error the user must acknowledge (e.g. a failed send)
  isLoading: boolean;
  isSyncing: boolean;
  isAuthenticated: boolean;
  composingCount: number;  // number of open composers — pauses disruptive refreshes

  setUiLang: (lang: UILang) => void;
  setActiveAccount: (id: string | null) => void;
  setActiveEmail: (id: string | null) => void;
  setActiveFolder: (folder: Folder) => void;
  setReplyMode: (mode: ReplyMode) => void;
  setSearchQuery: (q: string) => void;
  setEmailFilter: (f: EmailFilter) => void;
  setConversationView: (v: boolean) => void;
  setEmailDensity: (d: EmailDensity) => void;
  setReplyAllDefault: (v: boolean) => void;
  setDefaultAccount: (id: string | null) => void;
  moveAccount: (id: string, beforeId: string | null) => void;   // drag-reorder in the ACCOUNTS list
  setNavMore: (v: boolean) => void;
  setAcctMore: (v: boolean) => void;
  setNotifyNewMail: (v: boolean) => void;
  setEmailAppUnread: (v: boolean) => void;
  setUndoSendSecs: (n: number) => void;
  loadUndoSend: () => void;
  queueSend: (job: SendJob) => void;     // Send Now's last step, after the composer has closed
  undoSend: (id: string) => void;
  closeUndone: (id: string) => void;     // the reopened composer was closed
  setMailboxFilter: (ids: string[] | null) => void;
  setAuthenticated: (token: string) => void;
  logout: () => void;

  loadMe: () => Promise<void>;
  loadEmailPrefs: () => Promise<void>;   // never rejects
  loadAccounts: (opts?: { silent?: boolean }) => Promise<void>;
  loadFolders: (accountId?: string) => Promise<void>;
  // Resolves false on failure; `sorting` = emails already in the mailbox being filed by the label's filter.
  saveLabelOverride: (accountId: string, path: string, patch: { name?: string; color?: string | null; deleted?: boolean; rule?: LabelRule | null; applyExisting?: boolean }) => Promise<false | { sorting?: number }>;
  createLabel: (accountId: string, name: string, color: string) => Promise<void>;
  loadLabelUnread: () => void;
  loadFolderUnread: () => void;
  addAccount: (data: any) => Promise<void>;
  updateAccount: (id: string, data: any) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  syncAccount: (id: string, opts?: { auto?: boolean }) => Promise<void>;
  syncAll: () => Promise<void>;
  backgroundRefresh: () => Promise<void>;  // silent periodic refresh, skipped while composing
  incComposing: () => void;
  decComposing: () => void;

  loadEmails: (opts?: { silent?: boolean }) => Promise<void>;
  loadMoreEmails: () => Promise<void>;
  loadEmail: (id: string) => Promise<Email | null>;
  translateEmail: (id: string, lang: UILang) => Promise<string>;
  toggleStar: (emailId: string) => Promise<void>;
  setStar: (emailId: string, on: boolean) => Promise<void>;
  markRead: (emailId: string) => Promise<void>;
  markUnread: (emailId: string) => Promise<void>;
  deleteEmail: (emailId: string) => Promise<void>;
  deleteEmails: (emailIds: string[]) => Promise<void>;
  archiveEmail: (emailId: string) => Promise<void>;
  moveToSpam: (emailId: string) => Promise<void>;
  setLabel: (emailId: string, label: string, on: boolean) => Promise<void>;
  snoozeEmails: (emailIds: string[], until: number | null) => Promise<boolean>;   // until = unix seconds; null unsnoozes

  showToast: (message: string) => void;
  setBlockingError: (msg: string | null) => void;
  getActiveEmail: () => Email | null;
}

// Auto-sync throttle: opening the module / switching accounts kicks a background IMAP fetch,
// but at most once per account per minute (the server also syncs every minute on its own).
const lastAutoSync: Record<string, number> = {};
// Label unread counts refresh after each read/unread or label change; bulk actions make one call per
// message, so a burst collapses into a single fetch.
let labelUnreadTimer: ReturnType<typeof setTimeout> | undefined;
let folderUnreadTimer: ReturnType<typeof setTimeout> | undefined;
// Emails whose Delete is on its way to the server. Their rows leave the list at once, but the server
// answers only after its IMAP move/expunge — 5–7 seconds, longer while it retries — so until then
// every list reload leaves them out (loadEmails) instead of bringing a just-deleted row back.
const pendingDeletes = new Set<string>();
// ── The search box's wait, and the list's run number (Calem, report 2695ff3b) ──
// Typing in the search bubble used to ask the server once per keystroke, so "peter" asked five
// overlapping questions. One- and two-letter terms are too short for the emails index and fall back to
// reading every email (routes/emails.ts), which takes seconds, so "p" was still being answered long
// after "peter" had been — and whichever answered last filled the list, which is why a searched list
// came back looking unsearched. The typed words now go out once the typing stops, so a half-typed name
// is never asked for; `searchedQ` is the words the newest load asked for, so the same words are not
// asked for twice.
// The wait is 3 seconds HERE ONLY — every other search box keeps the 300ms of report 0c04879b. Email
// is the one list whose short terms take seconds to answer, and Calem asked for "a 3- or 4-second pause
// in case the user types in extra characters" (opening #2), so a name typed at any speed goes out once,
// whole. Nobody has to sit out the wait: Enter in the bubble, and its clear ×, search at once
// (components/EmailList).
let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchedQ = '';
const SEARCH_WAIT_MS = 3000;
// Every list load takes the next number. Its answer is dropped whole — rows AND total — once a newer
// load has started, so a slow query can no longer repaint a newer one's list.
let emailsRun = 0;
// Default mailbox and ACCOUNTS order — keyed by user id, since one office browser serves several
// people and a shared mailbox sits in several people's sidebars.
const defaultAccountKey = (userId: string) => `mf_defaultAccount:${userId}`;
const readDefaultAccount = (userId?: string): string | null => {
  if (!userId) return null;
  try { return localStorage.getItem(defaultAccountKey(userId)) || null; } catch { return null; }
};
const accountOrderKey = (userId: string) => `mf_accountOrder:${userId}`;
const readAccountOrder = (userId?: string): string[] => {
  if (!userId) return [];
  try {
    const ids = JSON.parse(localStorage.getItem(accountOrderKey(userId)) || '[]');
    return Array.isArray(ids) ? ids.filter(id => typeof id === 'string') : [];
  } catch { return []; }
};
type AccountListState = Pick<AppState, 'accounts' | 'accountOrder' | 'defaultAccountId'>;
// The ACCOUNTS list as shown (sidebar and rail): the default on top, then the rest in the user's own
// order — accounts added since go last, in the server's order, and removed ones drop out.
export const orderedAccounts = ({ accounts, accountOrder, defaultAccountId }: AccountListState): Account[] => {
  const list = accounts.filter(a => accountOrder.includes(a.id))
    .sort((a, b) => accountOrder.indexOf(a.id) - accountOrder.indexOf(b.id))
    .concat(accounts.filter(a => !accountOrder.includes(a.id)));
  const def = list.find(a => a.id === defaultAccountId);
  return def ? [def, ...list.filter(a => a !== def)] : list;
};
// The mailbox to open: the top of the ACCOUNTS list — the default while it is still in the list,
// otherwise the first in the user's order.
const accountToOpen = (s: AccountListState): string | null => orderedAccounts(s)[0]?.id || null;

// PandaAI Email layout on the server, so it follows the user to any browser (Calem, Bugify #a375cb1a).
// Every change is sent as it happens; the saves go one after another so a quick double toggle can't
// land out of order, and a failed one is ignored — this browser still has it in localStorage.
let emailPrefsSaves: Promise<unknown> = Promise.resolve();
export const saveEmailPrefs = (patch: Omit<EmailUiPrefs, 'updated_at'>) => {
  if (!localStorage.getItem('mf_token')) return;
  emailPrefsSaves = emailPrefsSaves.then(() => api.saveEmailPrefs(patch)).catch(() => {});
};
// The on/off choices and the localStorage keys ('1' / '0') this browser has always kept them under.
const PREF_FLAGS = {
  splitScreen: 'mf_splitScreen', sidebarCollapsed: 'mf_sidebarCollapsed', conversationView: 'mf_conversationView',
  navMore: 'mf_navMore2', acctMore: 'mf_acctMore',
} as const;
// The mode a Reply button opens (EmailDetail, EmailViewer): Reply All while the user's Reply all switch is on
// (Calem, report 643e2141). Reply All and Forward open as they are.
// Those views leave Reply out while the switch is on (opening #2), so this is the fallback for a Reply still drawn.
export const replyModeOf = (mode: ReplyMode): ReplyMode => (mode === 'reply' && useStore.getState().replyAllDefault ? 'replyAll' : mode);
// What the rows in the list answer (AppState.emailsFor).
export const emailsKey = (accountId: string | null | undefined, folder: Folder, search: string | null | undefined): string =>
  `${accountId || ''}|${folder}|${search || ''}`;

// ── Undo send (Calem, report ef1507ed) ──
// The waits the composer's Send menu offers, in seconds; 0 = Off.
export const UNDO_SEND_SECS = [0, 5, 10, 20];
const undoSecsOf = (v: any): number => (UNDO_SEND_SECS.includes(v) ? v : 0);
// One email leaving a composer (queueSend), which has saved it to Drafts and closed by now. `restore` + `reopen` are
// what Undo opens again: ComposeNew with the draft it was opened from, or Composer with the email it answers.
export interface SendJob {
  subject: string;                                // for the failure dialog
  payload: Parameters<typeof api.sendEmail>[0];
  clearDraft: () => Promise<void>;                // the composer's auto-saved draft — removed once sent
  draftId: () => string | null;                   // read on Undo, so a save still landing at Send is not missed
  restore: Omit<ComposeRestore, 'draftId'>;
  reopen: { kind: 'new'; draft?: Email } | { kind: 'reply'; email: Email; mode: ReplyMode };
}
export interface QueuedSend extends SendJob { id: string; sendAt: number }   // sendAt: ms
export interface UndoneSend { id: string; restore: ComposeRestore; reopen: SendJob['reopen'] }
const sendTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Whether this page has the user's saved choice: loadEmailPrefs (MailApp) brings it, a pick in the menu sets it, and a
// composer opened where MailApp isn't (the Leads and Starred viewers) asks for it (loadUndoSend).
let undoSendKnown = false;

// What the "Email failed to send" box says. An HTML error page (nginx's 504 when the server took
// too long) is not a reason a person can read — say what it means instead.
const failReason = (e: any) => e?.htmlPage ? i18n.t('eml_send_fail_page', { status: e.status }) : (e?.message || String(e));
// Posts one email — at once, or when its Undo send seconds are up. Success → its draft goes, a quiet toast and a
// silent list merge. Failure → the BLOCKING dialog; the auto-saved draft stays in Drafts to be sent again.
function postSend(job: SendJob) {
  useStore.getState().showToast('Sending…');
  api.sendEmail(job.payload).then(async () => {
    try { await job.clearDraft(); } catch {}
    const st = useStore.getState();
    st.showToast('Email sent successfully');
    st.loadEmails({ silent: true });
  }).catch((e: any) => {
    useStore.getState().setBlockingError(
      `"${job.subject}"\n\n${failReason(e)}\n\nYour message is saved in Drafts — open it there to send again.`
    );
  });
}

export const useStore = create<AppState>((set, get) => ({
  uiLang: resolveInitialLang(),
  me: null,
  accounts: [],
  folders: {},
  labelOverrides: {},
  labelUnread: {},
  folderUnread: {},
  emails: [],
  emailsFor: '',
  totalEmails: 0,
  emailLimit: 50,
  isLoadingMore: false,
  activeAccountId: null,
  activeEmailId: null,
  activeEmailPin: null,
  activeFolder: 'inbox',
  replyMode: 'reply',
  searchQuery: '',
  emailFilter: {},
  conversationView: (() => { try { return localStorage.getItem('mf_conversationView') !== '0'; } catch { return true; } })(),
  emailDensity: (() => { try { const v = localStorage.getItem('mf_emailDensity'); return v === 'comfortable' || v === 'compact' ? v : 'default'; } catch { return 'default'; } })(),
  replyAllDefault: (() => { try { return localStorage.getItem('mf_replyAllDefault') === '1'; } catch { return false; } })(),
  defaultAccountId: null,   // read in loadAccounts, once the user is known
  accountOrder: [],         // likewise
  navMore: (() => { try { return localStorage.getItem('mf_navMore2') === '1'; } catch { return false; } })(),
  acctMore: (() => { try { return localStorage.getItem('mf_acctMore') === '1'; } catch { return false; } })(),
  notifyNewMail: true,      // until loadEmailPrefs brings the saved one
  undoSendSecs: 0,          // likewise (or loadUndoSend)
  sendQueue: [],
  undoneSends: [],
  emailAppUnread: false,    // until loadEmailPrefs brings the saved one
  emailPrefsVersion: 0,
  mailboxFilter: null,
  toastMessage: null,
  blockingError: null,
  isLoading: false,
  isSyncing: false,
  isAuthenticated: !!localStorage.getItem('mf_token'),
  composingCount: 0,

  setUiLang: (lang) => {
    try { localStorage.setItem('mf_uiLang', lang); } catch { /* ignore */ }
    i18n.changeLanguage(lang);
    set({ uiLang: lang });
    // Mirror the choice server-side (best-effort); local storage stays the source of truth.
    if (localStorage.getItem('mf_token')) api.setLang(lang).catch(() => {});
  },

  setActiveAccount: (id) => {
    // Switching accounts always lands back on the Inbox (and resets the "Load more" window).
    set({ activeAccountId: id, activeEmailId: null, activeFolder: 'inbox', emailLimit: 50 });
    get().loadEmails();                                        // local mail — instant
    get().loadLabelUnread();
    if (id) get().syncAccount(id, { auto: true }).catch(() => {});   // fetch new mail behind it
  },
  setActiveEmail: async (id) => {
    const row = id ? get().emails.find(e => e.id === id) ?? null : null;
    set({ activeEmailId: id, activeEmailPin: row });
    if (id) {
      // Conversations: ThreadView fetches its own bodies and marks messages read itself (as they're
      // expanded, and the rest when the user leaves it) — and loadEmail must not run, because it
      // would replace the row with an object that has no thread fields, flipping the pane back to
      // the flat view.
      const row = get().emails.find(e => e.id === id);
      if ((row?.threadCount || 1) > 1) return;
      await get().markRead(id);
      await get().loadEmail(id);
    }
  },
  setActiveFolder: (folder) => {
    set({ activeFolder: folder, activeEmailId: null, emailLimit: 50 });
    get().loadEmails();
  },
  setReplyMode: (mode) => set({ replyMode: mode }),
  // The box shows the words at once; the list is asked for them once the typing stops (SEARCH_WAIT_MS
  // above). Every other path into the list — Enter and the clear × in the bubble, a mailbox, a folder,
  // the filter panel, a silent refresh — still asks immediately, as before: they all go through
  // loadEmails, which calls the wait off rather than asking the same question again a moment later.
  setSearchQuery: (q) => {
    set({ searchQuery: q, emailLimit: 50 });
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      searchTimer = undefined;
      if (get().searchQuery !== searchedQ) get().loadEmails();
    }, SEARCH_WAIT_MS);
  },
  // The filter panel's Search button, and each chip's × (which sends the rest of the boxes back).
  // Like a new search it starts the list again from the first 50 rows.
  setEmailFilter: (f) => {
    set({ emailFilter: f, emailLimit: 50 });
    get().loadEmails();
  },
  setConversationView: (v) => {
    try { localStorage.setItem('mf_conversationView', v ? '1' : '0'); } catch { /* ignore */ }
    set({ conversationView: v, activeEmailId: null, emailLimit: 50 });
    get().loadEmails();
    saveEmailPrefs({ conversationView: v });
  },
  setEmailDensity: (d) => {
    try { localStorage.setItem('mf_emailDensity', d); } catch { /* ignore */ }
    set({ emailDensity: d });
    saveEmailPrefs({ emailDensity: d });
  },
  setReplyAllDefault: (v) => {
    try { localStorage.setItem('mf_replyAllDefault', v ? '1' : '0'); } catch { /* ignore */ }
    set({ replyAllDefault: v });
    saveEmailPrefs({ replyAllDefault: v });
  },
  setDefaultAccount: (id) => {
    const userId = get().me?.userId;
    if (userId) {
      try {
        if (id) localStorage.setItem(defaultAccountKey(userId), id);
        else localStorage.removeItem(defaultAccountKey(userId));
      } catch { /* ignore */ }
    }
    set({ defaultAccountId: id });
    saveEmailPrefs({ defaultAccountId: id });
  },
  // `id` goes just above `beforeId` (null = the bottom). The default stays pinned on top — a drop
  // above it lands just below it — and keeps its own place in the saved order, so clearing its star
  // puts it back there rather than leaving it on top.
  moveAccount: (id, beforeId) => {
    const s = get();
    const saved = orderedAccounts({ ...s, defaultAccountId: null }).map(a => a.id);
    const pin = s.defaultAccountId && saved.includes(s.defaultAccountId) ? s.defaultAccountId : null;
    if (!saved.includes(id) || id === pin || id === beforeId) return;
    const order = saved.filter(x => x !== pin && x !== id);
    order.splice(beforeId === null ? order.length : Math.max(0, order.indexOf(beforeId)), 0, id);
    if (pin) order.splice(saved.indexOf(pin), 0, pin);
    // Under a mailbox filter (the Quick Launch focus tab) the list holds only some of the mailboxes: the others keep
    // their places in the saved order, and the ones shown fill their own places in the new order.
    let k = 0;
    const full = !s.mailboxFilter ? order
      : [...s.accountOrder, ...saved.filter(x => !s.accountOrder.includes(x))].map(x => (saved.includes(x) ? order[k++] : x));
    const userId = s.me?.userId;
    if (userId) {
      try { localStorage.setItem(accountOrderKey(userId), JSON.stringify(full)); } catch { /* ignore */ }
    }
    set({ accountOrder: full });
    saveEmailPrefs({ accountOrder: full });
  },
  setNavMore: (v) => {
    try { localStorage.setItem('mf_navMore2', v ? '1' : '0'); } catch { /* ignore */ }
    set({ navMore: v });
    saveEmailPrefs({ navMore: v });
  },
  setAcctMore: (v) => {
    try { localStorage.setItem('mf_acctMore', v ? '1' : '0'); } catch { /* ignore */ }
    set({ acctMore: v });
    saveEmailPrefs({ acctMore: v });
  },
  setNotifyNewMail: (v) => {
    set({ notifyNewMail: v });
    saveEmailPrefs({ notifyNewMail: v });
  },
  setEmailAppUnread: (v) => {
    set({ emailAppUnread: v });
    saveEmailPrefs({ emailAppUnread: v });
  },
  setUndoSendSecs: (n) => {
    undoSendKnown = true;
    set({ undoSendSecs: undoSecsOf(n) });
    saveEmailPrefs({ undoSendSecs: undoSecsOf(n) });
  },
  // Not without the Email module: a refused call pages the admins (and such a user has no mail to send).
  loadUndoSend: () => {
    if (undoSendKnown || !localStorage.getItem('mf_token') || !get().me?.modules.includes('email')) return;
    undoSendKnown = true;
    const before = get().undoSendSecs;
    api.getEmailPrefs().then(
      p => { if (get().undoSendSecs === before) set({ undoSendSecs: undoSecsOf(p?.undoSendSecs) }); },
      () => { undoSendKnown = false; },
    );
  },
  // Off → posted at once, as Send Now always was. Otherwise the email waits here, shown with its countdown and Undo,
  // and is posted when the time is up — the same post either way (postSend).
  queueSend: (job) => {
    const secs = get().undoSendSecs;
    if (!secs) { postSend(job); return; }
    const id = Math.random().toString(36).slice(2);
    sendTimers.set(id, setTimeout(() => {
      sendTimers.delete(id);
      const q = get().sendQueue.find(x => x.id === id);
      if (!q) return;
      set(s => ({ sendQueue: s.sendQueue.filter(x => x.id !== id) }));
      postSend(q);
    }, secs * 1000));
    set(s => ({ sendQueue: [...s.sendQueue, { ...job, id, sendAt: Date.now() + secs * 1000 }] }));
  },
  // Undo: the timer stops and the composer opens again (components/UndoSend) with the email as it was — nothing was
  // posted, and auto-save goes on with the same draft.
  undoSend: (id) => {
    const q = get().sendQueue.find(x => x.id === id);
    if (!q) return;   // its time was up — already on its way
    clearTimeout(sendTimers.get(id));
    sendTimers.delete(id);
    set(s => ({
      sendQueue: s.sendQueue.filter(x => x.id !== id),
      undoneSends: [...s.undoneSends, { id, reopen: q.reopen, restore: { ...q.restore, draftId: q.draftId() } }],
    }));
  },
  closeUndone: (id) => set(s => ({ undoneSends: s.undoneSends.filter(x => x.id !== id) })),
  // What is already loaded is narrowed at once, so no other mailbox shows while the list comes again, and the open
  // mailbox closes if the filter leaves it out. Loaded already → the list is fetched again under the new filter (a
  // mailbox it adds, or all of them once it is lifted) and the open mailbox's mail with it.
  setMailboxFilter: (ids) => {
    const s = get();
    if (JSON.stringify(ids) === JSON.stringify(s.mailboxFilter)) return;
    const accounts = ids ? s.accounts.filter(a => ids.includes(a.id)) : s.accounts;
    const keepOpen = !ids || accounts.some(a => a.id === s.activeAccountId);
    set({
      mailboxFilter: ids, accounts,
      ...(keepOpen ? {} : { activeAccountId: null, activeEmailId: null, activeEmailPin: null, emails: [], totalEmails: 0 }),
    });
    if (s.accounts.length) get().loadAccounts().then(() => get().loadEmails()).catch(() => {});
  },

  setAuthenticated: (token) => {
    setItemWithRoom('mf_token', token); // a full origin must never eat the login (incident 2026-09-23)
    set({ isAuthenticated: true });
  },
  logout: () => {
    setBooksSeat(false);   // the next login is not this one
    setAuditorSeat(false);
    // An email still waiting out Undo send is not posted under whoever signs in next: it stays in Drafts, as when the
    // tab is closed, and the dialog says so. Reopened composers close with the session.
    const waiting = get().sendQueue;
    sendTimers.forEach(timer => clearTimeout(timer));
    sendTimers.clear();
    undoSendKnown = false;
    localStorage.removeItem('mf_token');
    set({ isAuthenticated: false, me: null, accounts: [], emails: [], activeAccountId: null, activeEmailId: null, defaultAccountId: null, accountOrder: [],
      undoSendSecs: 0, sendQueue: [], undoneSends: [],
      ...(waiting.length ? { blockingError: `${waiting.map(q => `"${q.subject}"`).join('\n')}\n\n${i18n.t('eml_undo_signed_out')}` } : {}) });
  },

  // Load the current user's profile/role/modules (portal bootstrap).
  loadMe: async () => {
    if (!localStorage.getItem('mf_token')) return;
    try {
      // Multi-company (2026-09-22, per-user grants 2026-10-03): the stored company choice is per
      // user and survives only while /api/me still lists that company for them — so a revoked grant
      // or a shared browser can never carry one person's world into another's session.
      // The user id is read from the JWT FIRST so this very /api/me carries X-Active-Company — the
      // shell derives its whole world (modules, tabs, company name) from this one answer.
      bindActiveCompanyFromToken();
      let me = await api.getMe();
      bindActiveCompanyUser(me.userId);
      const chosen = getActiveCompanyId();
      if (chosen && !(me.companies || []).some(c => c.id === chosen)) clearActiveCompany();
      else if (chosen && me.activeCompanyId !== chosen) me = await api.getMe(); // header missed (token without userId): ask again, now bound
      setBrand(me.brand, me.firmLabels);
      setZoneCity((me as any).region?.timezone);
      setCurrencyWords(me.region);   // {{cur}} / $t(cur_word) in UI strings: the active company's currency
      setAuditorSeat(me.role === 'auditor' && !!me.acctWorkspace);   // (b13 S3) the auditor's read-only net on PND.1 / SSO in api.ts req() — only where the workspace is on (owner 2026-11-03)
      setBooksSeat(me.role === 'books');   // the read-only net in api.ts req()
      set({ me, isAuthenticated: true });
    } catch (e: any) {
      // A server restarting under a deploy is not a bad token (Bugify deploy-now, 2026-11-03): keep the sign-in, and let
      // AuthGate ask again once the API answers (App.tsx). Anything else — 401, an expired token — signs out as before.
      if (e?.apiDown) return;
      get().logout(); // invalid/expired token
    }
  },

  // The user's saved email layout (Calem, Bugify #a375cb1a). What the server holds wins: it goes into
  // this browser's localStorage keys and the store, and MailApp re-reads the ones it keeps itself.
  // Whatever the server doesn't hold yet (all of it, the first time) is sent up from this browser, so
  // the layout in use today becomes the saved one.
  loadEmailPrefs: async () => {
    const userId = get().me?.userId;
    if (!localStorage.getItem('mf_token') || !userId) return;
    const saved: EmailUiPrefs | null = await api.getEmailPrefs().catch(() => null);
    if (!saved) return;   // unreachable → this browser's copy stays
    undoSendKnown = true;
    const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
    const write = (k: string, v: string | null) => {
      try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* ignore */ }
    };
    const missing: Omit<EmailUiPrefs, 'updated_at'> = {};
    const widths: { sidebar?: number; list?: number } = {};
    for (const [col, k] of [['sidebar', 'mf_w_sidebar'], ['list', 'mf_w_list']] as const) {
      const w = saved.widths?.[col], mine = Math.round(Number(read(k)));
      if (w !== undefined) write(k, String(w));
      else if (mine >= 160 && mine <= 4000) widths[col] = mine;
    }
    if (widths.sidebar || widths.list) missing.widths = widths;
    for (const key of Object.keys(PREF_FLAGS) as (keyof typeof PREF_FLAGS)[]) {
      const k = PREF_FLAGS[key], v = saved[key], mine = read(k);
      if (v !== undefined) write(k, v ? '1' : '0');
      else if (mine === '1' || mine === '0') missing[key] = mine === '1';
    }
    const density = read('mf_emailDensity');
    if (saved.emailDensity) write('mf_emailDensity', saved.emailDensity);
    else if (density === 'default' || density === 'comfortable' || density === 'compact') missing.emailDensity = density;
    if (saved.defaultAccountId !== undefined) write(defaultAccountKey(userId), saved.defaultAccountId);
    else if (readDefaultAccount(userId)) missing.defaultAccountId = readDefaultAccount(userId);
    if (saved.accountOrder) write(accountOrderKey(userId), JSON.stringify(saved.accountOrder));
    else if (readAccountOrder(userId).length) missing.accountOrder = readAccountOrder(userId);
    // Reply all: the saved choice only, Off when none is saved. This browser's copy is never sent up as the
    // user's own, as the layout's are: one office browser serves several people, and this switch decides who
    // an answer goes to.
    write('mf_replyAllDefault', saved.replyAllDefault ? '1' : '0');
    const s = get();
    const conversationView = saved.conversationView ?? s.conversationView;
    set({
      conversationView,
      // As setConversationView does — a row opened in the other view isn't one in this one.
      ...(conversationView !== s.conversationView ? { activeEmailId: null, emailLimit: 50 } : {}),
      emailDensity: saved.emailDensity ?? s.emailDensity,
      replyAllDefault: saved.replyAllDefault === true,
      navMore: saved.navMore ?? s.navMore,
      acctMore: saved.acctMore ?? s.acctMore,
      notifyNewMail: saved.notifyNewMail !== false,
      undoSendSecs: undoSecsOf(saved.undoSendSecs),
      emailAppUnread: saved.emailAppUnread === true,
      defaultAccountId: saved.defaultAccountId !== undefined ? saved.defaultAccountId : readDefaultAccount(userId),
      accountOrder: saved.accountOrder ?? readAccountOrder(userId),
      emailPrefsVersion: s.emailPrefsVersion + 1,
    });
    if (Object.keys(missing).length) saveEmailPrefs(missing);
  },

  loadAccounts: async () => {
    if (!localStorage.getItem('mf_token')) return;
    try {
      const rows = await api.getAccounts();
      const filter = get().mailboxFilter;   // the focus dashboard's mailboxes, if this tab is one — as it is on arrival
      const accounts = rows.filter((a: any) => !filter || filter.includes(a.id));
      const mapped: Account[] = accounts.map((a: any) => ({
        id: a.id, name: a.name, email: a.email,
        imapHost: a.imap_host, imapPort: a.imap_port,
        smtpHost: a.smtp_host, smtpPort: a.smtp_port,
        myLang: a.my_lang, color: a.color, unread: a.unread || 0, attention: a.attention || 0,
        personal: !!a.personal, shared: !!a.shared,
      }));
      // Folder/label list is cached on each account row (refreshed at sync time); the local label
      // rename/colour/delete settings ride along with it.
      const folders: Record<string, FolderInfo[]> = {};
      const labelOverrides: Record<string, Record<string, LabelOverride>> = {};
      accounts.forEach((a: any) => {
        try { folders[a.id] = a.folders ? JSON.parse(a.folders) : []; }
        catch { folders[a.id] = []; }
        try { labelOverrides[a.id] = a.label_overrides ? JSON.parse(a.label_overrides) : {}; }
        catch { labelOverrides[a.id] = {}; }
      });
      const defaultAccountId = readDefaultAccount(get().me?.userId);
      const accountOrder = readAccountOrder(get().me?.userId);
      set({ accounts: mapped, folders, labelOverrides, defaultAccountId, accountOrder });
      // Under a mailbox filter the open mailbox is always one it allows — never one it leaves out.
      const open = get().activeAccountId;
      if (filter && open && !mapped.some(a => a.id === open)) set({ activeAccountId: null, activeEmailId: null, activeEmailPin: null });
      if (!get().activeAccountId && mapped.length > 0) {
        set({ activeAccountId: accountToOpen({ accounts: mapped, accountOrder, defaultAccountId }) });
      }
      get().loadLabelUnread();   // with the unread counts above — after every sync and background refresh
    } catch (e) {
      console.error('Load accounts error:', e);
    }
  },

  // Explicit refresh of one account's mailbox/label list (also arrives via loadAccounts).
  loadFolders: async (accountId) => {
    const id = accountId || get().activeAccountId;
    if (!id) return;
    try {
      const list = await api.getFolders(id);
      set(s => ({ folders: { ...s.folders, [id]: list } }));
    } catch { /* ignore — non-critical */ }
  },

  // Rename / recolour / delete / restore a label inside PandaAI Email only — the server stores it
  // locally and never touches Gmail. Deleting the label that is open sends the list back to Inbox.
  saveLabelOverride: async (accountId, path, patch) => {
    try {
      const { overrides, sorting } = await api.saveLabelOverride(accountId, { path, ...patch });
      set(s => ({ labelOverrides: { ...s.labelOverrides, [accountId]: overrides || {} } }));
      if (patch.deleted && get().activeAccountId === accountId) {
        const open = (get().folders[accountId] || []).find(f => f.path === get().activeFolder);
        if (open && labelView(open, overrides).hidden) get().setActiveFolder('inbox');
      }
      return { sorting };
    } catch (e: any) {
      // not_personal: labels are set up in a person's own mailbox only (server/src/routes/accounts.ts).
      get().showToast(i18n.t(e?.payload?.code === 'not_personal' ? 'eml_label_err_not_personal' : 'eml_label_save_failed'));
      return false;
    }
  },

  // New label — created on the mail server itself (on Gmail, a real Gmail label) so emails can be
  // filed under it; the colour is the usual local override. The refreshed label list comes back with
  // it. Throws, so the New label dialog can say why.
  createLabel: async (accountId, name, color) => {
    const { folders, overrides } = await api.createLabel(accountId, { name, color: color || null });
    set(s => ({
      folders: { ...s.folders, [accountId]: folders || [] },
      labelOverrides: { ...s.labelOverrides, [accountId]: overrides || {} },
    }));
  },

  // Unread count per label of the open mailbox, for the sidebar's label rows.
  loadLabelUnread: () => {
    get().loadFolderUnread();   // the folder rows refresh at the same moments
    if (labelUnreadTimer) clearTimeout(labelUnreadTimer);
    labelUnreadTimer = setTimeout(async () => {
      const id = get().activeAccountId;
      if (!id || !localStorage.getItem('mf_token')) return;
      try {
        const counts = await api.getLabelUnread(id);
        set(s => ({ labelUnread: { ...s.labelUnread, [id]: counts || {} } }));
      } catch { /* non-critical — the next refresh fills it in */ }
    }, 300);
  },

  // Unread count per fixed folder of the open mailbox — of every mailbox while none is open — for the
  // sidebar's folder rows (Drafts: every draft). Debounced like the label counts, and loaded with them
  // (loadLabelUnread).
  loadFolderUnread: () => {
    if (folderUnreadTimer) clearTimeout(folderUnreadTimer);
    folderUnreadTimer = setTimeout(async () => {
      if (!localStorage.getItem('mf_token')) return;
      if (get().mailboxFilter && !get().activeAccountId) return;   // 'all' would count the mailboxes it leaves out
      const key = get().activeAccountId || 'all';
      try {
        const counts = await api.getFolderUnread(key);
        set(s => ({ folderUnread: { ...s.folderUnread, [key]: counts || {} } }));
      } catch { /* non-critical — the next refresh fills it in */ }
    }, 300);
  },

  // Composing count — pauses background refresh while user edits an email
  incComposing: () => set(s => ({ composingCount: s.composingCount + 1 })),
  decComposing: () => set(s => ({ composingCount: Math.max(0, s.composingCount - 1) })),

  // Silent periodic refresh — never shows spinner/toast, never disrupts open composers
  backgroundRefresh: async () => {
    if (!localStorage.getItem('mf_token')) return;
    // CRITICAL: never touch the UI while the user is composing an email
    if (get().composingCount > 0) return;
    try {
      await get().loadEmails({ silent: true });
      await get().loadAccounts();  // refresh unread counts (already silent, no spinner)
    } catch {
      /* ignore — purely background */
    }
  },

  addAccount: async (data) => {
    const account = await api.addAccount(data);
    await get().loadAccounts();
    // Under a mailbox filter the new mailbox isn't one this tab shows, so the open one stays.
    if (!get().mailboxFilter) set({ activeAccountId: account.id });
    await get().loadEmails();
  },

  updateAccount: async (id, data) => {
    await api.updateAccount(id, data);
    await get().loadAccounts();
  },

  deleteAccount: async (id) => {
    await api.deleteAccount(id);
    const remaining = get().accounts.filter(a => a.id !== id);
    set({ accounts: remaining, activeAccountId: accountToOpen({ ...get(), accounts: remaining }) });
    await get().loadEmails();
  },

  // The list ALWAYS shows local mail instantly; the IMAP fetch runs behind it and the result is
  // merged in place with a silent reload (no page spinner, no visual reset).
  syncAccount: async (id, opts) => {
    if (opts?.auto) {
      // Opening a mailbox must NEVER wait on IMAP: queue the sync server-side (bg=1 returns
      // immediately) and pick up whatever it brings with a delayed silent refresh. The
      // every-minute server cron keeps mail fresh regardless.
      const now = Date.now();
      if (now - (lastAutoSync[id] || 0) < 60_000) return;   // server-side cron syncs every minute anyway
      lastAutoSync[id] = now;
      api.syncAccount(id, true).catch(() => {});
      setTimeout(() => {
        if (get().composingCount > 0) return;   // never touch the UI while the user is composing
        get().loadEmails({ silent: true }).catch(() => {});
      }, 20_000);
      return;
    }
    set({ isSyncing: true });
    try {
      const result: any = await api.syncAccount(id);
      lastAutoSync[id] = Date.now();
      await get().loadAccounts();
      await get().loadEmails({ silent: true });
      if (result.queued) {
        // The worker is still syncing (big mailbox) — it finishes in the background.
        get().showToast('Sync is taking a while — it continues in the background.');
        setTimeout(() => { if (get().composingCount === 0) get().loadEmails({ silent: true }).catch(() => {}); }, 30_000);
      } else {
        get().showToast(`Synced! ${result.newEmails} new email(s)`);
      }
    } catch (e: any) {
      get().showToast('Sync failed: ' + e.message);
    } finally {
      set({ isSyncing: false });
    }
  },

  syncAll: async () => {
    set({ isSyncing: true });
    try {
      for (const acc of get().accounts) {
        await api.syncAccount(acc.id).catch(() => {});
        await get().loadEmails({ silent: true });   // new mail trickles in account by account
        lastAutoSync[acc.id] = Date.now();
      }
      await get().loadAccounts();
      get().showToast('All accounts synced!');
    } finally {
      set({ isSyncing: false });
    }
  },

  loadEmails: async (opts) => {
    if (!localStorage.getItem('mf_token')) return;
    // This load is the newest question the list has been asked, and it carries whatever is in the search
    // box right now — so a typed query still waiting to go out has been asked by this load anyway, and
    // its wait is called off rather than fetching the same page twice a moment later.
    if (searchTimer) { clearTimeout(searchTimer); searchTimer = undefined; }
    searchedQ = get().searchQuery;
    const run = ++emailsRun;
    // Scheduled is its own list (components/ScheduledList), not an emails query.
    if (get().activeFolder === 'scheduled') { set({ emails: [], totalEmails: 0, isLoading: false }); return; }
    // A mailbox filter with no mailbox open (none of its mailboxes is left): nothing to list — a query without an
    // account would list every mailbox's mail.
    if (get().mailboxFilter && !get().activeAccountId) { set({ emails: [], totalEmails: 0, isLoading: false }); return; }
    const silent = opts?.silent === true;
    if (!silent) set({ isLoading: true });
    try {
      const view = {
        accountId: get().activeAccountId || undefined,
        folder: get().activeFolder,
        search: get().searchQuery || undefined,
        // The filter panel travels with the page AND with the unread pass below — both have to ask
        // the same question, or unread mail the filter excludes would be merged back onto the list.
        filter: get().emailFilter,
        conversationView: get().conversationView,
      };
      // Every unread row of the view comes with the page. The list floats unread rows above its READ
      // line, and those have to be ALL of them — what the sidebar badge counts — not only the ones
      // among the newest 50. Rows already on the page aren't added twice; the rest sort after the
      // page anyway, so they go at the end. Drafts and Snoozed keep their own order (no unread group
      // there). If only this call fails, the page still shows as before.
      const keepOrder = view.folder === 'drafts' || view.folder === 'snoozed';
      // Emails being deleted stay off the list until the server has answered (pendingDeletes) — also
      // the ones that were pending when this fetch went out, since its answer can predate the move.
      const deleting = new Set(pendingDeletes);
      const [pageRes, unreadRes] = await Promise.all([
        api.getEmails({ ...view, limit: get().emailLimit }),   // limit grows via "Load more"; silent refreshes keep the expanded window
        keepOrder ? null : api.getEmails({ ...view, unread: true }).catch(() => null),
      ]);
      // Another load has started while these two were out — a key in the search box, a mailbox, a
      // folder — and it owns the list now, so this answer is dropped whole: rows, unread rows and the
      // total together. A search that takes seconds can no longer land on top of a newer one's rows.
      if (run !== emailsRun) return;
      const onPage = new Set(pageRes.emails.map((e: any) => e.id));
      const emails = [...pageRes.emails, ...(unreadRes?.emails || []).filter((e: any) => !onPage.has(e.id))]
        .filter((e: any) => !deleting.has(e.id) && !pendingDeletes.has(e.id));
      const total = pageRes.total;
      // The list endpoint returns only a preview (no full body_html). Preserve any
      // already-loaded full body / translations so a refresh never blanks an open email.
      const prev = get().emails;
      set({
        emails: emails.map((e: any) => {
          const existing = prev.find(p => p.id === e.id);
          return {
            id: e.id,
            accountId: e.account_id,
            subject: e.subject,
            from: { name: e.from_name, email: e.from_email },
            to: JSON.parse(e.to_list || '[]'),
            cc: JSON.parse(e.cc_list || '[]'),
            bcc: [],
            date: new Date(e.date * 1000),
            snoozedUntil: e.snoozed_until ? new Date(e.snoozed_until * 1000) : null,
            lang: e.detected_lang,
            // Keep the full body we already fetched (list query only has preview)
            bodyOriginal: (e.body_html ?? existing?.bodyOriginal) || '',
            bodyTranslated: e.translations ? JSON.parse(e.translations) : (existing?.bodyTranslated || {}),
            attachments: JSON.parse(e.attachments || '[]'),
            unread: Boolean(e.unread),
            starred: Boolean(e.starred),
            folder: e.folder,
            labels: e.labels ? JSON.parse(e.labels) : [],
            lead: e.lead || undefined,
            preview: e.preview || '',
            threadId: e.thread_id || undefined,
            threadCount: e.thread_count || 1,
            threadUnread: e.thread_unread || 0,
            threadIds: e.thread_ids || [e.id],
            participants: e.participants || [],
            openedAt: e.opened_at || undefined,
          };
        }),
        emailsFor: emailsKey(view.accountId, view.folder, view.search),
        totalEmails: total,
      });
      // Keep the pinned active row fresh; if the refreshed page no longer contains it
      // (filter/search/window), the old pin stays so the open reading pane survives.
      const { activeEmailId } = get();
      if (activeEmailId) {
        const fresh = get().emails.find(e => e.id === activeEmailId)
          ?? get().emails.find(e => (e.threadIds || []).includes(activeEmailId));
        if (fresh) set({ activeEmailPin: fresh });
      }
    } catch (e) {
      console.error('Load emails error:', e);
    } finally {
      if (!silent) set({ isLoading: false });
    }
  },

  // "Load more" at the bottom of the list: widen the fetch window by 50 and silently re-fetch.
  loadMoreEmails: async () => {
    if (get().isLoadingMore) return;
    set({ isLoadingMore: true, emailLimit: get().emailLimit + 50 });
    try { await get().loadEmails({ silent: true }); }
    finally { set({ isLoadingMore: false }); }
  },

  loadEmail: async (id) => {
    try {
      const e = await api.getEmail(id);
      const email: Email = {
        id: e.id, accountId: e.account_id, subject: e.subject,
        from: { name: e.from_name, email: e.from_email },
        to: JSON.parse(e.to_list || '[]'),
        cc: JSON.parse(e.cc_list || '[]'),
        bcc: [],
        date: new Date(e.date * 1000),
        snoozedUntil: e.snoozed_until ? new Date(e.snoozed_until * 1000) : null,
        lang: e.detected_lang,
        bodyOriginal: e.body_html || '',
        bodyTranslated: JSON.parse(e.translations || '{}'),
        attachments: JSON.parse(e.attachments || '[]'),
        unread: false,
        starred: Boolean(e.starred),
        folder: e.folder,
        labels: e.labels ? JSON.parse(e.labels) : [],
      };
      // Keep the list-row-only fields (thread grouping, preview) — the single-email endpoint
      // doesn't return them, and losing threadCount would flip an open conversation to flat view.
      set(s => {
        const emails = s.emails.map(ex => ex.id === id ? {
          ...email,
          preview: ex.preview,
          lead: ex.lead,
          threadId: ex.threadId,
          threadCount: ex.threadCount,
          threadUnread: ex.threadUnread,
          threadIds: ex.threadIds,
          participants: ex.participants,
          openedAt: ex.openedAt,
        } : ex);
        const fresh = emails.find(e2 => e2.id === id);
        return { emails, activeEmailPin: s.activeEmailId === id && fresh ? fresh : s.activeEmailPin };
      });
      return email;
    } catch {
      return null;
    }
  },

  translateEmail: async (id, lang) => {
    const cached = get().emails.find(e => e.id === id)?.bodyTranslated[lang];
    if (cached) return cached;
    const { translated } = await api.translateEmail(id, lang);
    set(s => ({
      emails: s.emails.map(e => e.id === id
        ? { ...e, bodyTranslated: { ...e.bodyTranslated, [lang]: translated } }
        : e),
    }));
    return translated;
  },

  toggleStar: async (emailId) => {
    const email = get().emails.find(e => e.id === emailId);
    if (!email) return;
    const newVal = !email.starred;
    // Optimistic update
    set(s => ({ emails: s.emails.map(e => e.id === emailId ? { ...e, starred: newVal } : e) }));
    try {
      await api.patchEmail(emailId, { starred: newVal });
    } catch {
      // Revert on failure
      set(s => ({ emails: s.emails.map(e => e.id === emailId ? { ...e, starred: !newVal } : e) }));
    }
  },

  // Set star to an explicit state (whole-conversation star). Works for non-list ids too.
  setStar: async (emailId, on) => {
    set(s => ({ emails: s.emails.map(e => e.id === emailId ? { ...e, starred: on } : e) }));
    await api.patchEmail(emailId, { starred: on }).catch(() => {});
  },

  markRead: async (emailId) => {
    const email = get().emails.find(e => e.id === emailId);
    // Thread members aren't list rows — persist server-side and shrink the row aggregate.
    if (!email) {
      set(s => ({
        emails: s.emails.map(r => {
          if (r.id === emailId || !(r.threadIds || []).includes(emailId)) return r;
          const left = Math.max(0, (r.threadUnread || 0) - 1);
          return { ...r, threadUnread: left, unread: left > 0 };
        }),
      }));
      await api.patchEmail(emailId, { unread: false }).catch(() => {});
      get().loadLabelUnread();
      return;
    }
    if (!email.unread) return;
    // Marking the row itself read means the whole conversation is read (callers loop the
    // members first) — zero the aggregate so a later expand can't re-bold the row.
    set(s => ({
      emails: s.emails.map(e => e.id === emailId ? { ...e, unread: false, threadUnread: 0 } : e),
      accounts: s.accounts.map(a => a.id === email.accountId
        ? { ...a, unread: Math.max(0, a.unread - 1) } : a),
    }));
    await api.patchEmail(emailId, { unread: false }).catch(() => {});
    get().loadLabelUnread();
  },

  markUnread: async (emailId) => {
    const email = get().emails.find(e => e.id === emailId);
    if (!email) {
      set(s => ({
        emails: s.emails.map(r => {
          if (r.id === emailId || !(r.threadIds || []).includes(emailId)) return r;
          const tu = Math.min(r.threadCount || 1, (r.threadUnread || 0) + 1);
          return { ...r, threadUnread: tu, unread: true };
        }),
      }));
      await api.patchEmail(emailId, { unread: true }).catch(() => {});
      get().loadLabelUnread();
      return;
    }
    if (email.unread) return;
    set(s => ({
      emails: s.emails.map(e => e.id === emailId
        ? { ...e, unread: true, threadUnread: Math.max(1, e.threadUnread || 0) } : e),
      accounts: s.accounts.map(a => a.id === email.accountId
        ? { ...a, unread: a.unread + 1 } : a),
    }));
    await api.patchEmail(emailId, { unread: true }).catch(() => {});
    get().loadLabelUnread();
  },

  // The row leaves the list at once, as Archive's does — the server's IMAP move (in Trash, the
  // permanent expunge) is what takes the seconds. Until the server answers, the id sits in
  // pendingDeletes so no refresh brings the row back. The backend retries a transient IMAP failure on
  // its own (see server/src/routes/emails.ts), so a failure reaching here is a real one: the list
  // reloads — the server still has the email, so its row comes back — and a toast says so.
  deleteEmail: async (emailId) => {
    const email = get().emails.find(e => e.id === emailId);
    if (!email) return;
    pendingDeletes.add(emailId);
    set(s => ({
      emails: s.emails.filter(e => e.id !== emailId),
      activeEmailId: s.activeEmailId === emailId ? null : s.activeEmailId,
    }));
    const ok = await api.deleteEmail(emailId).then(() => true, () => false);
    pendingDeletes.delete(emailId);
    if (ok) return;
    get().showToast('Failed to delete the email — please try again.');
    await get().loadEmails({ silent: true });
  },

  // Bulk delete — ONE server round-trip → ONE IMAP connection per account (no per-message
  // connection storm), so a whole conversation of any size (2 or 200) deletes in a single call.
  // The rows leave the list at once, like a single Delete; any id the server couldn't delete (even
  // after its own retries) comes back with the reload that follows, and a toast says how many. An id
  // already on its way (Delete pressed again on a row that has just gone) isn't sent a second time.
  deleteEmails: async (emailIds) => {
    const ids = emailIds.filter(id => !pendingDeletes.has(id));
    if (!ids.length) return;
    const idSet = new Set(ids);
    ids.forEach(id => pendingDeletes.add(id));
    set(s => ({
      emails: s.emails.filter(e => !idSet.has(e.id)),
      activeEmailId: s.activeEmailId && idSet.has(s.activeEmailId) ? null : s.activeEmailId,
    }));
    // null = the call itself failed, so nothing was deleted.
    const failed = await api.bulkDeleteEmails(ids).then(r => r.failed || [], () => null);
    ids.forEach(id => pendingDeletes.delete(id));
    if (failed && !failed.length) return;
    get().showToast(failed ? `Failed to delete ${failed.length} of ${ids.length} email(s).` : 'Delete failed — please try again.');
    await get().loadEmails({ silent: true });
  },

  archiveEmail: async (emailId) => {
    const email = get().emails.find(e => e.id === emailId);
    if (!email) { await api.patchEmail(emailId, { folder: 'archive' }).catch(() => {}); return; }
    // Remove from current view immediately
    set(s => ({
      emails: s.emails.filter(e => e.id !== emailId),
      activeEmailId: s.activeEmailId === emailId ? null : s.activeEmailId,
    }));
    try {
      await api.patchEmail(emailId, { folder: 'archive' });
    } catch {
      await get().loadEmails();
    }
  },

  moveToSpam: async (emailId) => {
    const email = get().emails.find(e => e.id === emailId);
    if (!email) return;
    // Remove from current view immediately; reload to restore correct state if the server op fails.
    set(s => ({
      emails: s.emails.filter(e => e.id !== emailId),
      activeEmailId: s.activeEmailId === emailId ? null : s.activeEmailId,
    }));
    try {
      await api.moveEmail(emailId, 'spam');
    } catch {
      await get().loadEmails();
    }
  },

  // Adding a label files an Inbox email out of the Inbox, like Gmail's "Move to" (the server moves it
  // too — POST /emails/:id/label). In the Inbox list its row leaves at once — the whole conversation
  // when a dragged thread is labelled message by message — along with its share of the Inbox counts,
  // and the reading pane closes if it had that email open. A failed call puts the row back.
  setLabel: async (emailId, label, on) => {
    const email = get().emails.find(e => e.id === emailId);
    const prev = email ? [...email.labels] : null;
    const row = on && get().activeFolder === 'inbox'
      ? get().emails.find(e => e.id === emailId || (e.threadIds || []).includes(emailId)) : undefined;
    const rowIdx = row ? get().emails.indexOf(row) : -1;
    const rowUnread = row?.unread ? Math.max(1, row.threadUnread || 0) : 0;
    if (row) {
      set(s => ({
        emails: s.emails.filter(e => e.id !== row.id),
        totalEmails: Math.max(0, s.totalEmails - 1),
        accounts: s.accounts.map(a => a.id === row.accountId ? { ...a, unread: Math.max(0, a.unread - rowUnread) } : a),
        activeEmailId: s.activeEmailId && (s.activeEmailId === row.id || (row.threadIds || []).includes(s.activeEmailId))
          ? null : s.activeEmailId,
      }));
    } else {
      // Optimistic label update.
      set(s => ({ emails: s.emails.map(e => e.id === emailId
        ? { ...e, labels: on ? Array.from(new Set([...e.labels, label])) : e.labels.filter(l => l !== label) }
        : e) }));
    }
    try {
      await api.setEmailLabel(emailId, label, on);
    } catch {
      if (row) {
        // Unless a refresh already brought it back — that refresh recounted, so don't count it twice.
        if (!get().emails.some(e => e.id === row.id)) set(s => ({
          emails: [...s.emails.slice(0, rowIdx), row, ...s.emails.slice(rowIdx)],
          totalEmails: s.totalEmails + 1,
          accounts: s.accounts.map(a => a.id === row.accountId ? { ...a, unread: a.unread + rowUnread } : a),
        }));
      } else if (prev) set(s => ({ emails: s.emails.map(e => e.id === emailId ? { ...e, labels: prev } : e) }));
      get().showToast('Could not update label on the server');
    }
    get().loadLabelUnread();
  },

  // Snooze (until = unix seconds) or, with null, unsnooze — a conversation's Inbox messages together.
  // Either way the email leaves the folder on screen, so its list row goes at once (and the reading
  // pane closes if it showed it); then the list and the Inbox counts reload from the server, which
  // also puts back anything that failed.
  snoozeEmails: async (emailIds, until) => {
    if (!emailIds.length) return false;
    const ids = new Set(emailIds);
    const gone = new Set(get().emails.filter(e => ids.has(e.id) || (e.threadIds || []).some(id => ids.has(id))).map(e => e.id));
    set(s => ({
      emails: s.emails.filter(e => !gone.has(e.id)),
      activeEmailId: s.activeEmailId && (ids.has(s.activeEmailId) || gone.has(s.activeEmailId)) ? null : s.activeEmailId,
    }));
    const results = await Promise.all(emailIds.map(id => api.snoozeEmail(id, until).then(() => true, () => false)));
    await Promise.all([get().loadEmails({ silent: true }), get().loadAccounts()]);
    return results.every(Boolean);
  },

  showToast: (message) => {
    set({ toastMessage: message });
    setTimeout(() => set({ toastMessage: null }), 3000);
  },

  setBlockingError: (msg) => {
    set({ blockingError: msg });
  },

  getActiveEmail: () => {
    const { emails, activeEmailId, activeEmailPin } = get();
    if (!activeEmailId) return null;
    return emails.find(e => e.id === activeEmailId)
      // A new reply moves the conversation row onto a newer message id — follow the thread
      // instead of blanking the reading pane.
      ?? emails.find(e => (e.threadIds || []).includes(activeEmailId))
      // Row fell out of the refreshed page (unread filter / search / paging) — pin keeps it open.
      ?? (activeEmailPin && (activeEmailPin.id === activeEmailId
            || (activeEmailPin.threadIds || []).includes(activeEmailId)) ? activeEmailPin : null);
  },
}));
